import React, { useState } from 'react';

import { DualScrollTable } from '../components/tabela';
import { exportarFechadosXLSX } from '../lib/xlsx';
import { fmt, getRecLabel, getRecMonths } from '../lib/formato';

function RepAssesView({ leads, onToggle, onTogglePaid }) {
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterParceiro, setFilterParceiro] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const closed = leads.filter(l => l.stage === 'fechado');

  const recLabel  = key => getRecLabel(key);
  const recMonths = key => getRecMonths(key);
  const tipo      = key => (!key || key==='unica') ? 'Avulso' : 'Contínuo';

  // Parceiros únicos para filtro
  const allParceiros = [...new Set(
    closed.flatMap(l => (l.partners||[]).map(p => p.name)).filter(Boolean)
  )].sort();

  const filtered = closed.filter(l => {
    if (search && !l.company_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTipo && tipo(l.recurrence) !== filterTipo) return false;
    if (filterParceiro && !(l.partners||[]).some(p => p.name === filterParceiro)) return false;
    if (filterStatus === 'ativo'   && l.contract_paused) return false;
    if (filterStatus === 'pausado' && !l.contract_paused) return false;
    return true;
  });

  // Totais — considera apenas contratos não pausados
  const ativos = filtered.filter(l => !l.contract_paused);
  const totalMensal   = ativos.reduce((s,l) => s + (Number(l.value)||0), 0);
  const totalRepasse  = ativos.reduce((s,l) => {
    const v = Number(l.value)||0;
    return s + (l.partners||[]).reduce((sp,p) => sp + v*(Number(p.commission_rate)||0)/100, 0);
  }, 0);
  const totalLiquido  = totalMensal - totalRepasse;
  // Quanto do repasse já foi marcado como pago (roadmap #6)
  const repassePago = ativos.reduce((s,l) => {
    const v = Number(l.value)||0;
    return s + (l.partners||[]).filter(p => p.paid_at).reduce((sp,p) => sp + v*(Number(p.commission_rate)||0)/100, 0);
  }, 0);
  const pausados = filtered.filter(l => l.contract_paused).length;

  return <div className="space-y-5">
    {/* Cards de resumo */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="acard p-4">
        <div className="text-xs text-gray-400 font-medium mb-1">Clientes Fechados</div>
        <div className="text-2xl font-bold text-gray-900">{filtered.length}</div>
        <div className="flex gap-2 mt-1">
          <span className="text-xs text-green-600 font-medium">{ativos.length} ativos</span>
          {pausados > 0 && <span className="text-xs text-orange-500 font-medium">{pausados} pausados</span>}
        </div>
      </div>
      <div className="acard p-4">
        <div className="text-xs text-gray-400 font-medium mb-1">Recebimento Mensal</div>
        <div className="text-xl font-bold text-green-600">{fmt(totalMensal)}</div>
        <div className="text-xs text-gray-400 mt-0.5">contratos ativos (não pausados)</div>
      </div>
      <div className="acard p-4">
        <div className="text-xs text-gray-400 font-medium mb-1">Total Repasse/mês</div>
        <div className="text-xl font-bold text-purple-600">{fmt(totalRepasse)}</div>
        <div className="text-xs text-gray-400 mt-0.5">{totalMensal>0 ? `${(totalRepasse/totalMensal*100).toFixed(1)}% do recebimento` : '—'}</div>
        {totalRepasse>0 && <div className="text-xs mt-0.5"><span className="text-emerald-600 font-medium">{fmt(repassePago)} pago</span><span className="text-gray-300"> · </span><span className="text-amber-600 font-medium">{fmt(totalRepasse-repassePago)} a pagar</span></div>}
      </div>
      <div className="acard p-4">
        <div className="text-xs text-gray-400 font-medium mb-1">Líquido Mensal</div>
        <div className="text-xl font-bold text-blue-600">{fmt(totalLiquido)}</div>
        <div className="text-xs text-gray-400 mt-0.5">após repasses</div>
      </div>
    </div>

    {/* Filtros */}
    <div className="flex gap-2 flex-wrap items-center">
      <input className="apple-input flex-1 min-w-48 px-3 py-2 text-sm"
        placeholder="🔍 Buscar empresa..."
        value={search} onChange={e=>setSearch(e.target.value)} />
      <select className="apple-input px-3 py-2 text-sm" value={filterTipo} onChange={e=>setFilterTipo(e.target.value)}>
        <option value="">Todos os tipos</option>
        <option value="Contínuo">Contínuo</option>
        <option value="Avulso">Avulso</option>
      </select>
      <select className="apple-input px-3 py-2 text-sm" value={filterParceiro} onChange={e=>setFilterParceiro(e.target.value)}>
        <option value="">Todos os parceiros</option>
        {allParceiros.map(p=><option key={p} value={p}>{p}</option>)}
      </select>
      <select className="apple-input px-3 py-2 text-sm" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
        <option value="">Todos os status</option>
        <option value="ativo">✅ Ativos</option>
        <option value="pausado">⏸ Pausados</option>
      </select>
      <button onClick={()=>exportarFechadosXLSX(leads)}
        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-full font-semibold shadow-sm whitespace-nowrap transition-colors">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
        Exportar .xlsx
      </button>
    </div>

    {/* Tabela */}
    {filtered.length === 0
      ? <div className="acard p-10 text-center text-gray-400">Nenhum cliente fechado encontrado.</div>
      : <div className="acard overflow-hidden">
          <DualScrollTable
            filtered={filtered}
            tipo={tipo}
            recMonths={recMonths}
            recLabel={recLabel}
            onToggle={onToggle}
            onTogglePaid={onTogglePaid}
            totalMensal={totalMensal}
            totalRepasse={totalRepasse}
          />
        </div>}
  </div>;
}

// ─── MainApp ──────────────────────────────────────────────

export { RepAssesView };
